import React, { useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import GalleryModal from 'src/design-system/components/molecules/GalleryModal/GalleryModal';
import { getSignedUrlApi } from '@api/getSignedUrl';
import { Tabs, message, Modal } from 'src/design-system/components/atoms';
import { useTheme } from 'src/theming/useTheme';

const { TabPane } = Tabs;

interface TabConfig {
  label: string;
  imageArray?: any[];
  videoArray?: any[];
  toFetchSignedUrl?: boolean;
}

const GalleryModalWithSignedUrl = (props: any) => {
  const {
    imageArray = [],
    videoArray = [],
    toFetchSignedUrl = false,
    placeholderText = 'View',
    tabs
  } = props;

  const theme = useTheme();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isGalleryModalVisible, setIsGalleryModalVisible] = useState(false);
  const [galleryModalData, setGalleryModalData] = useState({ videos: [] });
  const [tabsModalData, setTabsModalData] = useState<Array<{ mediaList: any[]; videos: any[] }>>([]);

  const isTabsMode = tabs && Array.isArray(tabs) && tabs.length > 0;
  const [mediaList, setMediaList] = useState([]);

  const fetchSignedUrls = async (urls) => {
    return await Promise.all(
      urls.map(async (url) => {
        const response = await getSignedUrlApi(url);
        if (!response.isSuccess || !response?.data?.signedUrl) {
          throw new Error('Failed to fetch signed URLs');
        }
        return response.data.signedUrl;
      })
    );
  };

  const fetchSignedUrlsAndOpenModal = async () => {
    try {
      setIsImageLoading(true);


      // Tabs mode
      if (isTabsMode) {
        const tabsData = await Promise.all(
          tabs.map(async (tab: TabConfig) => {
            const tabToFetchSignedUrl = tab.toFetchSignedUrl ?? toFetchSignedUrl;
            const images = tabToFetchSignedUrl && tab.imageArray?.length
              ? await fetchSignedUrls(tab.imageArray)
              : (tab.imageArray || []);
            const videos = tabToFetchSignedUrl && tab.videoArray?.length
              ? await fetchSignedUrls(tab.videoArray)
              : (tab.videoArray || []);
            // Use mediaList for images to enable automatic type detection (PDF, image, video)
            return { mediaList: images, videos };
          })
        );
        setTabsModalData(tabsData);
        setIsGalleryModalVisible(true);
      } else {
        // Images go through mediaList for automatic type detection (could be PDFs, etc.)
        const images = toFetchSignedUrl ? await fetchSignedUrls(imageArray) : imageArray;
        // Videos are explicitly known to be videos
        const videos = toFetchSignedUrl ? await fetchSignedUrls(videoArray) : videoArray;

        setMediaList(images);
        setGalleryModalData({ videos });
        setIsGalleryModalVisible(true);
      }
    } catch (error) {
      message.error(error.message || 'Failed to open gallery');
    } finally {
      setIsImageLoading(false);
    }
  };

  // Check if there's any content to show
  const hasContent = isTabsMode
    ? tabs.some((tab: TabConfig) => (tab.imageArray?.length || 0) + (tab.videoArray?.length || 0) > 0)
    : imageArray.length > 0 || videoArray.length > 0;

  if (!hasContent) {
    return <span>Not available</span>;
  }

  // Tabs mode
  if (isTabsMode) {
    return (
      <>
        <span>
          <a onClick={fetchSignedUrlsAndOpenModal}>{placeholderText}</a>
          {isImageLoading && <LoadingOutlined style={{ marginLeft: theme.spacing[2] }} />}
        </span>
        <Modal
          visible={isGalleryModalVisible}
          onCancel={() => setIsGalleryModalVisible(false)}
          footer={null}
          destroyOnClose
          width="90%"
          title="Delivery Images"
        >
          <Tabs defaultActiveKey="0">
            {tabs.map((tab: TabConfig, index: number) => {
              const imageCount = (tab.imageArray?.length || 0);
              const videoCount = (tab.videoArray?.length || 0);
              const totalCount = imageCount + videoCount;
              const tabLabel = totalCount > 0 ? `${tab.label} (${totalCount})` : tab.label;
              
              return (
                <TabPane 
                  tab={tabLabel}
                  key={index.toString()}
                >
                  <GalleryModal
                    isVisible={true}
                    imageVideoGalleryData={{ videos: tabsModalData[index]?.videos || [] }}
                    mediaList={tabsModalData[index]?.mediaList || []}
                    handleModalClose={() => {}}
                    noModalWrapper={true}
                  />
                </TabPane>
              );
            })}
          </Tabs>
        </Modal>
      </>
    );
  }

  // Single modal mode (backward compatible)
  return (
    <>
      <span>
        <a onClick={fetchSignedUrlsAndOpenModal}>{placeholderText}</a>
        {isImageLoading && <LoadingOutlined style={{ marginLeft: theme.spacing[2] }} />}
      </span>
      {isGalleryModalVisible && (
        <GalleryModal
          isVisible={isGalleryModalVisible}
          imageVideoGalleryData={galleryModalData}
          mediaList={mediaList}
          handleModalClose={() => {
            setIsGalleryModalVisible(false);
            setMediaList([]);
          }}
        />
      )}
    </>
  );
};

export default GalleryModalWithSignedUrl;
